import React from 'react'

import {
  AlignItems,
  Box,
  Display,
  DisplayPosition,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { StandardRecipeItem } from '@library/api-menu-experience'

import { SoldOutOverlay } from '../SoldOutOverlay'
import { Banner } from '../RecipeTile.styles'

type RecipeBannerProps = {
  isOutOfStock: boolean
  recipe: StandardRecipeItem
}

type RecipeBannerTags = NonNullable<StandardRecipeItem['tagGroups']['banner']>['tags']

const WideTileBannerTags = withCitrusTheme(Box, () => ({
  display: 'contents',
  '@container (max-width: 352px)': {
    display: 'none',
  },
}))

const NarrowTileBannerTags = withCitrusTheme(Box, () => ({
  display: 'none',
  '@container (max-width: 352px)': {
    display: 'contents',
  },
}))

const renderBannerTags = (tags: RecipeBannerTags) =>
  tags.map((tag, index) => (
    <React.Fragment key={`${tag.type}-${tag.text}`}>
      {index > 0 ? (
        <Text aria-hidden="true" size={2} style={{ color: 'inherit' }}>
          |
        </Text>
      ) : null}
      <Text size={2} style={{ color: 'inherit', whiteSpace: 'nowrap' }}>
        {tag.text}
      </Text>
    </React.Fragment>
  ))

export const RecipeBanner = ({ recipe, isOutOfStock }: RecipeBannerProps) => {
  const banner = recipe.tagGroups.banner

  if (recipe.displayType !== 'ready-to-heat' || !banner || banner.tags.length === 0) {
    return null
  }

  const narrowTags = banner.tags.filter((tag) => tag.type !== 'fiveADay')

  return (
    <Banner
      data-testid="recipe-tile-banner"
      position={DisplayPosition.Relative}
      style={{
        backgroundColor: banner.colors?.backgroundColor.light ?? '#BB4213',
        color: banner.colors?.color.light ?? '#FFFFFF',
      }}
    >
      <SoldOutOverlay isOutOfStock={isOutOfStock} />
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceEvenly}
        width="100%"
      >
        <WideTileBannerTags data-testid="recipe-tile-banner-tags-wide">
          {renderBannerTags(banner.tags)}
        </WideTileBannerTags>
        <NarrowTileBannerTags data-testid="recipe-tile-banner-tags-narrow">
          {renderBannerTags(narrowTags)}
        </NarrowTileBannerTags>
      </Box>
    </Banner>
  )
}
